import { useState, useEffect } from 'react';
import { _tekmirioInfo, _tekmirioFiles, _info } from '../../../lib/static/oa/tekmirio/details';
import { mapByRefValue } from '../../../components/Details/Details';
import { fetchErgoDetails, fetchForeasDetails, fetchSullogesDetails, fetchTekmirioDetails, fetchTekmirioMediaDetails } from '../../../lib/fetchers';
import { useParams } from 'react-router-dom';
import { Box, Divider, Typography } from '@mui/material';
import Theme from '../../../Themes/main.theme';
import DetailsTekmirio from './DetailsTekmirio';
import { getMediaAndMetadata } from './helperFunctions';
import { IMediaAttachments, IMediaImagesVideos } from './types';
import { useSnackbar } from 'notistack';
import { mapRefValuesToHorizontalDetails } from '../../../components/Details/HorizonticalWrapper';
import { documentDetails, mapDocumentDetails } from './helperFuncs';
import DetailsOASkeleton from '../../../components/Skeletons/DetailsOASkeleton';
import { useContext } from 'react';
import BreadcrumbContext from '../../../components/context/BreadcrumbContext';
import { useTranslation } from 'react-i18next';
import EmptyPage from '../../../components/EmptyPage/EmptyPage';
import i18n from '../../../i18n';
import { transformGreekToEnglish } from '../../../lib/helperFuncs';

const SmartDetailsTekmirio = () => {
  const { id } = useParams();

  const [info, setInfo] = useState<any>(_info);
  const [basicInfo, setBasicInfo] = useState<any>([]);
  const [fileInfo, setFileInfo] = useState<any>(_tekmirioFiles);
  const [title, setTitle] = useState<string>('');
  const [titleEN, setTitleEN] = useState<string>('');
  const [media, setMedia] = useState<IMediaImagesVideos[]>([]);
  const [attachments, setAttachments] = useState<IMediaAttachments[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [errorMsg, setErrorMsg] = useState<string | any>();

  const { setName } = useContext(BreadcrumbContext.BreadcrumbProvider.context);

  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const fetcher = async () => {
    try {
      setName('');
      const detailsResp = await fetchTekmirioDetails(id);
      
      if (!detailsResp?.['o:is_public']) throw new Error;

      const collection = await fetchSullogesDetails(detailsResp['documents:documentsRelationCollection'][0]['value_resource_id']);
      
      const foreas = {
        title: collection.item?.['collections:collectionsRelateOrganisation']?.[0]?.['display_title'],
        id: collection.item?.['collections:collectionsRelateOrganisation']?.[0]?.['value_resource_id']
      };
      
      const collection_en_title = collection.item?.['collections:collectionsEnTitle']?.[0]?.['@value'];    
      const mediaArr = detailsResp?.['o:media']?.map((x: { ['@id']: string;['o:id']: number; }) => x?.['o:id']);

      const [ergo, foreas_details, mediaResp] =
      await Promise.all([
        fetchErgoDetails(detailsResp['documents:documentsRelationProject'][0]['value_resource_id']),
        fetchForeasDetails(foreas?.id),
        fetchTekmirioMediaDetails(mediaArr)
      
      ]);
      

      const ergo_en_title = ergo.item?.['projects:projectEnTitle']?.[0]?.['@value'];
      

      const foreas_en_title = foreas_details.item?.['organisations:organisationsEnNameEn']?.[0]?.['@value'];
      
      
      setName(i18n.language === 'el'
        ? detailsResp?.item?.['o:title']
        : detailsResp?.['documents:documentsEnTitle']?.[0]?.['@value']);
      document.title = (i18n.language === 'el'
        ? detailsResp?.item?.['o:title']
        : detailsResp?.['documents:documentsEnTitle']?.[0]?.['@value']);
      const { images, videos, attachments } = getMediaAndMetadata(mediaResp);
      
      setMedia([...images, ...videos]);
      setAttachments([...attachments]);

      setTitle(detailsResp?.['o:title']);
      setTitleEN(detailsResp?.['documents:documentsEnTitle']?.[0]?.['@value']);

      const _mappedMetadata = mapDocumentDetails(detailsResp, foreas, 
        ergo_en_title, collection_en_title, foreas_en_title);
      
      const _resourceIds = {
        documentsRelationProject: `/search-projects/details/${[_mappedMetadata.projectResourceId]}`,
        documentsRelationProjectEn: `/search-projects/details/${[_mappedMetadata.projectResourceId]}`,
        documentsRelationCollectionEn: `/collections/details/${[_mappedMetadata.collectionResourceId]}`,
        documentsRelationCollection: `/collections/details/${[_mappedMetadata.collectionResourceId]}`,
        documentsRelationOrganisation: `/search-organisation/details/${[_mappedMetadata.organisationResourceId]}`,
        documentsRelationOrganisationEn: `/search-organisation/details/${[_mappedMetadata.organisationResourceId]}`,
      };

      const mappedOmekaToRefValue =
        mapByRefValue(_mappedMetadata, _tekmirioInfo(_mappedMetadata).map(x => {
          return {
            ...x, name: t(x.name)
          };
        }));
        
      const tran = mappedOmekaToRefValue.map((x: any) => {
        return {
          ...x, value: i18n.language === 'el'
            ? x.value
            : x.refValue !=='documentsYtUrl'
              ? transformGreekToEnglish(t(x.value))
              : x.value
        };
      }); 
      const horizontalRefValue = documentDetails;
      const newValue = mapRefValuesToHorizontalDetails(horizontalRefValue, tran);

      const mappedOmekaToRefValues =
        mapByRefValue(_mappedMetadata, _info(_mappedMetadata).map(x => {
          return {
            ...x, name: t(x.name)
          };
        }));

      const newValues = mappedOmekaToRefValues.map(x => {        
        return { ...x, url: _resourceIds[x.refValue as keyof typeof _resourceIds] };
      });
      
      setInfo(newValues);
      setBasicInfo(newValue);
      setFileInfo(mediaResp);
    
      setIsLoading(false);
    } catch (error) {
      enqueueSnackbar(t('Παρουσιάστηκε σφάλμα, παρακαλούμε προσπαθήστε αργότερα'), { variant: 'error' });
      console.error(error);
      setErrorMsg(t('Η σελίδα που ζητήσατε δεν βρέθηκε.'));
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetcher();
  }, []);

  return (
    <>
    
      {isLoading
        ? <DetailsOASkeleton length={4} />
        : errorMsg ? 
          <Box id='container-details' sx={{ m: 2, height: '600px' }}>
            <EmptyPage errorMsg={errorMsg} />
          </Box>
          : <Box id='container-details' 
            width='100%'>
            <Typography sx={{ my: 2 }} variant='h2'
              color={Theme?.labelColor?.main}>
              {title}
            </Typography>
            <Typography sx={{ my: 2 }} variant='h2'
              color={Theme?.labelColor?.main}>
              {titleEN}
            </Typography>
            <Divider
              sx={{ mb: 5, border: 1, color: Theme?.dividerColor?.main }}
            />
            <DetailsTekmirio
              info={info}
              basic_info={basicInfo}
              file_info={fileInfo}
              media={media}
              attachments={attachments}
            />
          </Box>
      }
    </>
  );
};

export default SmartDetailsTekmirio;